import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/api";
import { toast } from "sonner";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

export default function SignUp() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      // Backend register sets cookie; mirror it via the existing login flow afterward isn't needed
      // because the register response sets the auth cookie directly. We use a fetch-style call.
      const { api } = await import("../lib/api");
      await api.post("/auth/register", form);
      // Refresh AuthContext by logging in (or just calling /auth/me). Easiest: call login() again.
      await login(form.email, form.password);
      toast.success(`Welcome to the Forge, ${form.name.split(" ")[0]}.`);
      navigate("/account");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="signup-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md border border-white/10 p-8 lg:p-10 bg-[#0E0E0E]">
        <img src={LOGO_URL} alt="MileForge" className="h-14 w-auto object-contain mb-3 -ml-3" />
        <div className="overline mb-1">Join The Forge</div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Create Account</h1>
        <p className="text-sm text-white/55 font-body mt-2">Faster checkout. Order tracking. Members-only drops.</p>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="overline block mb-2">Full Name</label>
            <input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          <div>
            <label className="overline block mb-2">Email</label>
            <input data-testid="signup-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input data-testid="signup-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body" />
          </div>
          {error && <div data-testid="signup-error" className="text-sm text-[#D96A1B] font-body">{error}</div>}
          <button data-testid="signup-submit" disabled={loading} className="btn-orange w-full justify-center">
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-sm font-body text-white/60 text-center">
          Already a rider? <Link to="/signin" data-testid="link-to-signin" className="text-[#D96A1B] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
